import React, { useCallback, useEffect, useState } from 'react';
import { useHtmlLang } from '@ds/react-utils';
import { setLang } from '@ds/base';
import { apiService, configuration, monitoring } from 'services';
import { IConfigurationInitResponse } from 'services/Configuration';

import './App.css';
import { Branding, defaultBranding } from 'store/types/branding';
import { BrowserRouter } from 'react-router-dom';
import { ApplicationLayout } from '../layouts';
import { VerifyApplicationContent, VerifyApplicationFooter, VerifyApplicationHeader } from './partialsInk';
import VerifyApplicationContext, { IVerifyApplicationContext } from 'contexts/VerifyApplicationContext';

function App() {
    const [context, setContext] = useState<IVerifyApplicationContext>({
        configurationState: 'Loading',
        displayChannel: 'SMSandPhoneCall',
        isDocumentLocked: false,
        phoneNumberDigitCheck: 'None',
        language: useHtmlLang() || 'en',
        branding: defaultBranding,
        customBranding: undefined,
        exception: undefined,
    });
    const [loading, setLoading] = useState<Boolean>(false);

    /**
     * Configure custom application branding
     */
    const loadAppBranding = useCallback(async (): Promise<Branding> => {
        try {
            const branding = await apiService.Get<Branding>('/branding');
            
            if (!branding) {
                return defaultBranding;
            }
            
            return branding;
        } catch (err) {
            // We do nothing and keep the default branding
            return defaultBranding;
        }
    }, []);

    const setLocale = (locale: any) => {
        setLang(locale);
    };

    useEffect(() => {
        if (!loading) {
            setLoading(true);

            // only let the application proceed when both configuration, branding, and phone number format are resolved
            Promise.all([configuration.initialize(), loadAppBranding()]).then((value: [IConfigurationInitResponse, Branding]) => {
                let config: IConfigurationInitResponse;
                let branding: Branding;
                [config, branding] = value;
                
                if (branding?.language) {
                    setLocale(branding.language);
                }

                monitoring.initialize(branding?.language)

                setContext({
                    configurationState: config.state,
                    displayChannel: config.supportedDisplayChannel,
                    isDocumentLocked: config.state === 'Locked',
                    phoneNumberDigitCheck: config.phoneNumberDigitCheck,
                    language: branding?.language || context.language,
                    branding: branding,
                    customBranding: {
                        buttonPrimaryBackground: branding?.colors?.find(c => c.name === 'buttonPrimaryBackground')?.value,
                        buttonPrimaryText: branding?.colors?.find(c => c.name === 'buttonPrimaryText')?.value
                    },
                    exception: config.exception,
                });
            });
        }
    }, [context, loadAppBranding, loading]);

    if (context.configurationState === 'Loading') {
        return <></>;
    }

    return (
        <VerifyApplicationContext.Provider value={context}>
            <BrowserRouter>
                <ApplicationLayout
                    header={VerifyApplicationHeader}
                    content={VerifyApplicationContent}
                    footer={VerifyApplicationFooter}
                    accountBranding={context.customBranding}
                />
            </BrowserRouter>
        </VerifyApplicationContext.Provider>
    );
}

export default App;
